import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Select, Spinner, Text,
  Badge, Container, VStack, useToast, Button, HStack, Input, InputGroup,
  InputLeftElement, SimpleGrid, Card, CardBody, Stat, StatLabel, StatNumber,
  StatHelpText, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
  ModalBody, ModalCloseButton, Progress, Tag, Tooltip
} from '@chakra-ui/react';
import { 
  CheckCircleIcon, WarningIcon, EmailIcon, ViewIcon, 
  SearchIcon, TimeIcon, CheckIcon, CloseIcon 
} from '@chakra-ui/icons';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

export default function LiveAttendanceReport() {
  const { user } = useContext(AuthContext);
  const toast = useToast();
  
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Timeline Modal State
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [selectedStudentTimeline, setSelectedStudentTimeline] = useState(null);

  // Fetch available meetings
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const response = await axiosInstance.get('/live-attendance/meetings');
        setMeetings(response.data); // array of { meetingId, roomName }
      } catch (error) {
        toast({
          title: 'خطأ في تحميل الحصص',
          description: error.response?.data?.error || error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };
    fetchMeetings();
  }, [toast]);

  // Fetch report when meeting is selected
  useEffect(() => {
    if (!selectedMeeting) {
      setReportData([]);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/live-attendance/report/${selectedMeeting}`);
        setReportData(response.data);
      } catch (error) {
        toast({
          title: 'خطأ في تحميل التقرير',
          description: error.response?.data?.error || error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [selectedMeeting, toast]);

  const handleSendSMS = async () => {
    if (!selectedMeeting) return;
    setSmsLoading(true);
    try {
      const response = await axiosInstance.post(`/live-attendance/send-absent-sms/${selectedMeeting}`);
      toast({
        title: 'تم الإرسال بنجاح',
        description: `تم وضع ${response.data.count} رسالة في طابور الإرسال للطلاب الغائبين أو المتوقفين عن المشاهدة لأكثر من 15 دقيقة.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'خطأ في إرسال الرسائل',
        description: error.response?.data?.error || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSmsLoading(false);
    }
  };

  const openTimeline = (record) => {
    setSelectedStudentTimeline(record);
    setIsTimelineOpen(true);
  };

  const formatDuration = (seconds) => {
    const sVal = Math.max(0, Math.round(seconds || 0));
    const totalMinutes = Math.floor(sVal / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const s = sVal % 60;
    if (h > 0) {
      return `${h}س ${m}د ${s}ث`;
    }
    return `${m}د ${s}ث`;
  };

  // Filtered report data based on search and status filter
  const filteredReportData = useMemo(() => {
    return reportData.filter((record) => {
      const student = record.studentId || {};
      const username = (student.username || '').toLowerCase();
      const phone = (student.phoneNumber || student.parentPhoneNumber || student.whatsappNumberParent || '').toLowerCase();
      const query = searchQuery.toLowerCase().trim();

      const matchesSearch = !query || username.includes(query) || phone.includes(query);
      if (!matchesSearch) return false;

      const total = record.totalDuration || 0;
      const present = record.presentDuration || 0;
      const percent = total > 0 ? (present / total) * 100 : 0;

      if (statusFilter === 'excellent') return percent >= 80;
      if (statusFilter === 'partial') return percent >= 50 && percent < 80;
      if (statusFilter === 'absent') return percent < 50;

      return true;
    });
  }, [reportData, searchQuery, statusFilter]);

  // Summary statistics
  const stats = useMemo(() => {
    const totalStudents = reportData.length;
    if (totalStudents === 0) return { totalStudents: 0, avgPresent: 0, excellentCount: 0, absentCount: 0 };

    let sumPresent = 0;
    let excellentCount = 0;
    let absentCount = 0;

    reportData.forEach((rec) => {
      const total = rec.totalDuration || 0;
      const present = rec.presentDuration || 0;
      sumPresent += present;
      const percent = total > 0 ? (present / total) * 100 : 0;
      if (percent >= 80) excellentCount += 1;
      else if (percent < 50) absentCount += 1;
    });

    return {
      totalStudents,
      avgPresent: Math.round(sumPresent / totalStudents),
      excellentCount,
      absentCount
    };
  }, [reportData]);

  return (
    <Container maxW="container.xl" py={8} dir="rtl">
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="blue.600">
              تقرير حضور الحصص المباشرة
            </Heading>
            <Text color="gray.500" fontSize="sm" mt={1}>
              تتبع مدة الحضور الفعلية في التطبيق وتسجيل التوقف عن المشاهدة كغياب
            </Text>
          </Box>
          {selectedMeeting && reportData.length > 0 && (
            <Button 
              colorScheme="red" 
              leftIcon={<EmailIcon />} 
              onClick={handleSendSMS}
              isLoading={smsLoading}
              boxShadow="sm"
            >
              إرسال تنبيهات غياب (أكثر من 15 دقيقة)
            </Button>
          )}
        </HStack>

        {/* Meeting Selection Card */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
          <Text mb={2} fontWeight="bold" color="gray.700">اختر الحصة المباشرة:</Text>
          <Select 
            placeholder="-- اختر الحصة المباشرة لعرض التقرير --" 
            value={selectedMeeting} 
            onChange={(e) => setSelectedMeeting(e.target.value)}
            size="lg"
            bg="gray.50"
            borderColor="gray.300"
          >
            {meetings.map((meeting) => (
              <option key={meeting.meetingId} value={meeting.meetingId}>
                {meeting.roomName}
              </option>
            ))}
          </Select>
        </Box>

        {/* Stats Overview */}
        {selectedMeeting && reportData.length > 0 && (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
            <Card bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <CardBody>
                <Stat>
                  <StatLabel color="gray.500">إجمالي الحضور</StatLabel>
                  <StatNumber color="blue.600">{stats.totalStudents}</StatNumber>
                  <StatHelpText>طالب انضم للحصة</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <CardBody>
                <Stat>
                  <StatLabel color="gray.500">متوسط مدة المشاهدة</StatLabel>
                  <StatNumber color="teal.600">{formatDuration(stats.avgPresent)}</StatNumber>
                  <StatHelpText>في التطبيق</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <CardBody>
                <Stat>
                  <StatLabel color="gray.500">حضور ممتاز (≥ 80%)</StatLabel>
                  <StatNumber color="green.600">{stats.excellentCount}</StatNumber>
                  <StatHelpText>متابعة كاملة</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <CardBody>
                <Stat>
                  <StatLabel color="gray.500">توقف عن المشاهدة / غياب</StatLabel>
                  <StatNumber color="red.500">{stats.absentCount}</StatNumber>
                  <StatHelpText>أقل من 50% حضور</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>
        )}

        {/* Search & Filter Bar */}
        {selectedMeeting && reportData.length > 0 && (
          <HStack spacing={4} wrap="wrap">
            <InputGroup maxW={{ base: "100%", md: "350px" }} bg="white" borderRadius="lg">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input 
                placeholder="ابحث بالاسم أو رقم الهاتف..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Select 
              maxW={{ base: "100%", md: "220px" }} 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              bg="white"
              borderRadius="lg"
            >
              <option value="all">كل الحالات</option>
              <option value="excellent">حضور ممتاز (≥ 80%)</option>
              <option value="partial">حضور جزئي (50% - 79%)</option>
              <option value="absent">توقف / غياب مستمر (&lt; 50%)</option>
            </Select>
          </HStack>
        )}

        {loading ? (
          <Box textAlign="center" py={12} bg="white" borderRadius="xl" boxShadow="sm">
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text mt={4} color="gray.600" fontWeight="medium">جاري تحميل تقرير الحضور...</Text>
          </Box>
        ) : selectedMeeting && filteredReportData.length > 0 ? (
          <Box bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflowX="auto">
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>اسم الطالب</Th>
                  <Th>رقم الهاتف</Th>
                  <Th>إجمالي الحصة</Th>
                  <Th>مدة الحضور في التطبيق</Th>
                  <Th>مدة التوقف / الغياب</Th>
                  <Th>نسبة الحضور</Th>
                  <Th>الحالة</Th>
                  <Th>المخطط الزمني</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredReportData.map((record) => {
                  const total = record.totalDuration || 0;
                  const present = record.presentDuration || 0;
                  const absent = record.absentDuration || 0;
                  const presentPercent = total > 0 ? Math.min(100, Math.round((present / total) * 100)) : 0;
                  
                  const student = record.studentId || {};
                  const phone = student.phoneNumber || student.parentPhoneNumber || student.whatsappNumberParent || 'غير متوفر';

                  return (
                    <Tr key={record._id} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                      <Td fontWeight="bold" color="gray.800">
                        {student.username || 'طالب غير معروف'}
                        {student.group && (
                          <Tag size="sm" colorScheme="purple" mr={2}>
                            {student.group}
                          </Tag>
                        )}
                      </Td>
                      <Td dir="ltr" textAlign="right" color="gray.600">{phone}</Td>
                      <Td fontWeight="medium">{formatDuration(total)}</Td>
                      <Td>
                        <Tag colorScheme="green" size="md" fontWeight="bold">
                          <CheckIcon mr={1} boxSize={3} />
                          {formatDuration(present)}
                        </Tag>
                      </Td>
                      <Td>
                        <Tag colorScheme={absent > 900 ? "red" : absent > 0 ? "orange" : "gray"} size="md" fontWeight="bold">
                          <CloseIcon mr={1} boxSize={2.5} />
                          {formatDuration(absent)}
                        </Tag>
                      </Td>
                      <Td minW="130px">
                        <HStack spacing={2}>
                          <Progress 
                            value={presentPercent} 
                            colorScheme={presentPercent >= 80 ? "green" : presentPercent >= 50 ? "yellow" : "red"} 
                            size="sm" 
                            borderRadius="full" 
                            flex="1" 
                          />
                          <Text fontSize="xs" fontWeight="bold" color="gray.600">{presentPercent}%</Text>
                        </HStack>
                      </Td>
                      <Td>
                        {presentPercent >= 80 ? (
                          <Badge colorScheme="green" px={2.5} py={1} borderRadius="md" display="inline-flex" alignItems="center">
                            <CheckCircleIcon mr={1}/> حضور ممتاز
                          </Badge>
                        ) : presentPercent >= 50 ? (
                          <Badge colorScheme="yellow" px={2.5} py={1} borderRadius="md" display="inline-flex" alignItems="center">
                            <WarningIcon mr={1}/> حضور جزئي
                          </Badge>
                        ) : (
                          <Badge colorScheme="red" px={2.5} py={1} borderRadius="md" display="inline-flex" alignItems="center">
                            <WarningIcon mr={1}/> غياب مستمر
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Button 
                          size="sm" 
                          colorScheme="blue" 
                          variant="outline" 
                          leftIcon={<ViewIcon />} 
                          onClick={() => openTimeline(record)}
                          borderRadius="lg"
                        >
                          عرض المخطط
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        ) : selectedMeeting && reportData.length > 0 ? (
          <Box textAlign="center" py={10} bg="white" borderRadius="xl" color="gray.500">
            لا توجد نتائج مطابقة لبحثك.
          </Box>
        ) : selectedMeeting ? (
          <Box textAlign="center" py={12} bg="white" borderRadius="xl" color="gray.500">
            لا توجد سجلات حضور مسجلة لهذه الحصة بعد.
          </Box>
        ) : null}
      </VStack>

      {/* Timeline Modal */}
      <Modal isOpen={isTimelineOpen} onClose={() => setIsTimelineOpen(false)} size="3xl" isCentered motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent dir="rtl" borderRadius="2xl" overflow="hidden" boxShadow="2xl">
          <ModalHeader bg="blue.600" color="white" borderBottomWidth="1px" py={4}>
            المخطط الزمني للحضور والتوقف عن المشاهدة
          </ModalHeader>
          <ModalCloseButton color="white" left="4" right="auto" top="3" />
          <ModalBody pb={6} pt={6} bg="gray.50">
            {selectedStudentTimeline && (
              <Box>
                {/* Summary Header */}
                <HStack justify="space-between" mb={6} bg="white" p={4} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" wrap="wrap" gap={3}>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.500">اسم الطالب</Text>
                    <Text fontWeight="bold" fontSize="lg" color="gray.800">
                      {selectedStudentTimeline.studentId?.username || 'غير معروف'}
                    </Text>
                  </VStack>
                  <HStack spacing={4}>
                    <VStack align="center" spacing={1}>
                      <Text fontSize="xs" color="gray.500">مدة الحضور</Text>
                      <Tag colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
                        {formatDuration(selectedStudentTimeline.presentDuration)}
                      </Tag>
                    </VStack>
                    <VStack align="center" spacing={1}>
                      <Text fontSize="xs" color="gray.500">مدة الغياب / التوقف</Text>
                      <Tag colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="full">
                        {formatDuration(selectedStudentTimeline.absentDuration)}
                      </Tag>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="xs" color="gray.500">إجمالي الحصة</Text>
                      <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
                        {formatDuration(selectedStudentTimeline.totalDuration)}
                      </Badge>
                    </VStack>
                  </HStack>
                </HStack>
                
                {(!selectedStudentTimeline.timeline || selectedStudentTimeline.timeline.length === 0) ? (
                  <Box textAlign="center" py={10} bg="white" borderRadius="xl" border="1px dashed" borderColor="gray.300">
                    <WarningIcon boxSize={10} color="orange.300" mb={4} />
                    <Text color="gray.600" fontSize="lg">لا توجد بيانات مخطط زمني مسجلة لهذا الطالب.</Text>
                    <Text color="gray.400" fontSize="sm" mt={2}>
                      يتم حفظ المخطط الزمني تلقائياً لحظة بلحظة أثناء متابعة الحصة.
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing={6} align="stretch">
                    {/* Visual Animated Timeline Bar */}
                    <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                      <Text fontWeight="bold" mb={4} color="gray.700">الشريط الزمني المرئي لحضور الطالب</Text>
                      
                      <style>
                        {`
                          @keyframes expandWidth {
                            from { width: 0%; opacity: 0; }
                            to { opacity: 1; }
                          }
                          .animated-bar {
                            animation: expandWidth 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards;
                            transform-origin: right;
                          }
                        `}
                      </style>

                      <Box w="100%" h="44px" bg="gray.200" borderRadius="xl" position="relative" overflow="hidden" display="flex" boxShadow="inner">
                        {selectedStudentTimeline.timeline.map((event, index, arr) => {
                          if (index === arr.length - 1) return null;
                          
                          const startTime = new Date(event.timestamp);
                          const endTime = new Date(arr[index + 1].timestamp);
                          const durationSeconds = Math.max(0, (endTime.getTime() - startTime.getTime()) / 1000);
                          
                          const totalMeetingSeconds = selectedStudentTimeline.totalDuration || 1;
                          const widthPercent = Math.max((durationSeconds / totalMeetingSeconds) * 100, 1);
                          
                          const isPresent = event.status === 'present';
                          const color = isPresent ? 'linear-gradient(90deg, #48BB78 0%, #38A169 100%)' : 'linear-gradient(90deg, #F56565 0%, #E53E3E 100%)';
                          
                          const timeString = `${startTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn', hour: '2-digit', minute:'2-digit', second:'2-digit' })} إلى ${endTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn', hour: '2-digit', minute:'2-digit', second:'2-digit' })}`;

                          return (
                            <Tooltip 
                              key={index}
                              label={`${isPresent ? 'حاضر في التطبيق' : 'توقف عن المشاهدة / غائب'}: من ${timeString} (${formatDuration(durationSeconds)})`}
                              hasArrow
                            >
                              <Box 
                                h="100%"
                                className="animated-bar"
                                style={{ width: `${widthPercent}%`, background: color }}
                                borderRight="2px solid white"
                                _last={{ borderRight: "none" }}
                                _hover={{ filter: "brightness(1.15)", cursor: "pointer" }}
                                transition="all 0.2s"
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                      
                      <HStack mt={5} justify="center" spacing={8}>
                        <HStack>
                          <Box w="14px" h="14px" bg="#38A169" borderRadius="full" boxShadow="sm"/>
                          <Text fontSize="sm" fontWeight="medium" color="gray.700">فترة حضور ومشاهدة في التطبيق</Text>
                        </HStack>
                        <HStack>
                          <Box w="14px" h="14px" bg="#E53E3E" borderRadius="full" boxShadow="sm"/>
                          <Text fontSize="sm" fontWeight="medium" color="gray.700">توقف عن المشاهدة / غياب</Text>
                        </HStack>
                      </HStack>
                    </Box>

                    {/* Detailed Event Log */}
                    <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" maxH="380px" overflowY="auto">
                      <Text fontWeight="bold" mb={4} color="gray.700">سجل الأحداث التفصيلي</Text>
                      <VStack align="stretch" spacing={3}>
                        {selectedStudentTimeline.timeline.map((event, index, arr) => {
                          if (index === arr.length - 1) return null;

                          const startTime = new Date(event.timestamp);
                          const endTime = new Date(arr[index + 1].timestamp);
                          const durationSeconds = Math.max(0, (endTime.getTime() - startTime.getTime()) / 1000);
                          const isPresent = event.status === 'present';

                          return (
                            <HStack key={index} spacing={3} p={3} borderRadius="lg" bg={isPresent ? "green.50" : "red.50"} borderWidth="1px" borderColor={isPresent ? "green.100" : "red.100"}>
                              <Box>
                                {isPresent ? (
                                  <CheckCircleIcon boxSize={6} color="green.500" />
                                ) : (
                                  <WarningIcon boxSize={6} color="red.500" />
                                )}
                              </Box>
                              
                              <Box flex="1">
                                <HStack justify="space-between">
                                  <Text fontWeight="bold" fontSize="sm" color={isPresent ? "green.800" : "red.800"}>
                                    {isPresent ? 'حاضر ويشاهد الحصة في التطبيق' : 'توقف عن المشاهدة (تبديل النافذة / تصغير التطبيق / غياب)'}
                                  </Text>
                                  <Badge colorScheme={isPresent ? "green" : "red"} variant="solid" borderRadius="full" px={2}>
                                    {formatDuration(durationSeconds)}
                                  </Badge>
                                </HStack>
                                <Text fontSize="xs" color="gray.600" mt={1}>
                                  من {startTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn' })} إلى {endTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn' })}
                                </Text>
                              </Box>
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>

                  </VStack>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter bg="white" borderTopWidth="1px" py={4}>
            <Button onClick={() => setIsTimelineOpen(false)} colorScheme="blue" px={8} borderRadius="lg">
              إغلاق
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Container>
  );
}

